import * as t from 'io-ts';
import { NumberReflectModeC } from '../../constants/appSettings';
import { booleanFromString0or1, nullableArray, numberFromString, stringEnum } from './codecs';

export enum StaffStatus {
  Offline = '0',
  Free = '100',
  Busy = '200',
  Rest = '300',
  AfterWorkCountDown = '400',
  AfterWork = '500',
  NotReady = '700',
}
export const Staff = t.type({
  avatar: t.string,
  ctime: numberFromString,
  kickOff: booleanFromString0or1,
  mobile: t.string,
  mtime: numberFromString,
  nickName: t.string,
  roleId: t.string,
  sdkAppId: t.string,
  skillGroupId: t.string,
  staffName: t.string,
  staffNo: t.string,
  useMobile: t.string, // '0 1 2'
  userId: t.string,
  wechatMiniProgramAccept: booleanFromString0or1,
  status: t.union([t.undefined, stringEnum(StaffStatus)]),
});

export type StaffTypes = t.TypeOf<typeof Staff>;

export const SkillGroup = t.type({
  skillGroupId: t.string,
  skillGroupName: t.string,
  extraFlag: t.union([t.literal('IM'), t.literal('ALL'), t.literal('TEL')]),
  ctime: t.string,
  mtime: t.string,
  servingNum: t.string,
  maxConcurrency: t.string,
});

export type SkillGroupTypes = t.TypeOf<typeof SkillGroup>;

export const RoleItem = t.type({
  roleId: t.string,
  roleName: t.string,
});

const AppSettingsC = t.partial({
  hideCalloutNumber: booleanFromString0or1,
  hideCallinNumber: booleanFromString0or1,
  callTimeout: numberFromString,
  mobileSwitchDisplay: booleanFromString0or1,
  imTimeout: numberFromString,
  endCountdown: numberFromString,
  imEndCountdown: numberFromString,
  omEndCountdown: numberFromString,
  callOutNum: t.string,
  imAutoAccept: booleanFromString0or1,
  telAutoAccept: booleanFromString0or1,
  smsPrivilege: booleanFromString0or1,
  telCallinPostIVR: booleanFromString0or1,
  telCalloutPostIVR: booleanFromString0or1,
  loginStaffStatus: t.string,
  numberReflectMode: NumberReflectModeC,
  asr: t.boolean,
  seatType: t.union([t.literal(1), t.literal(2), t.literal(3), t.literal(4), t.literal(5), t.literal(6), t.literal(7)]),
  realtimeAsr: t.boolean,
});

export const adminAPIs = {
  '/tcccadmin/app/getAppInfo': {
    Input: t.type({}),
    Output: AppSettingsC,
  },
  '/tcccadmin/staff/getRestReasonList': {
    Input: t.type({}),
    Output: t.type({
      restReasonList: t.array(
        t.type({
          name: t.string,
          restId: t.string,
          sortId: t.string,
          state: t.string,
        }),
      ),
    }),
  },
  '/tcccadmin/staff/getStaffList': {
    Input: t.type({
      userId: t.string,
      staffName: t.string,
      roleId: t.string,
      mobile: t.string,
      staffNo: t.string,
      batchUserIds: t.union([t.undefined, t.array(t.string)]),
      skillGroupId: t.union([t.undefined, t.string]),
      fuzzingKeyWord: t.union([t.undefined, t.string]),
      pageSize: t.union([t.undefined, numberFromString]),
      orderField: t.union([t.undefined, t.string]),
      orderType: t.union([t.undefined, t.string]),
      pageNum: t.union([t.undefined, numberFromString]),
      isNeedStatus: t.union([t.undefined, t.boolean]),
      status: t.array(t.number),
      statusRefreshTime: t.union([
        t.undefined,
        t.type({
          from: t.string,
          to: t.string,
        }),
      ]),
    }),
    Output: t.type({
      total: numberFromString,
      staffList: nullableArray(Staff),
    }),
  },
  '/tcccadmin/app/getInternalCdr': {
    Input: t.type({
      staff: t.type({
        sdkAppId: t.string,
        userId: t.string,
      }),
      startTimestamp: t.number,
      endTimestamp: t.number,
      caller: t.string,
      callee: t.string,
      user: t.string,
    }),
    Output: t.type({
      total: t.number,
      data: t.union([
        t.null,
        t.array(
          t.type({
            beginTimestamp: t.number,
            endTimestamp: t.number,
            caller: t.string,
            callee: t.string,
          }),
        ),
      ]),
    }),
  },
  '/tcccadmin/tech/getSkillGroupInfo': {
    Input: t.type({
      sdkAppId: t.string,
      skillGroupIdList: t.array(t.string),
    }),
    Output: t.type({
      skillGroupList: t.array(SkillGroup),
    }),
  },
  '/tcccadmin/user/getUserMark': {
    Input: t.type({
      sdkAppId: t.string,
      userId: t.string,
    }),
    Output: t.type({
      userMark: t.string,
    }),
  },
  '/tcccadmin/ivr/updateIvr': {
    Input: t.type({
      ivrId: t.string,
      ivrName: t.string,
      ivrJsonStr: t.string,
      publish: t.string,
      type: t.union([t.literal('0'), t.literal('1'), t.literal('2'), t.literal('3'), t.literal('4')]),
    }),
    Output: t.type({}),
  },
  '/tcccadmin/ivr/addIvr': {
    Input: t.type({
      ivrName: t.string,
      ivrJsonStr: t.string,
      publish: t.string,
      type: t.union([t.literal('0'), t.literal('1'), t.literal('2'), t.literal('3'), t.literal('4')]),
    }),
    Output: t.type({}),
  },
  '/tcccadmin/ivr/updateVersionIvr': {
    Input: t.type({
      ivrName: t.string,
      ivrId: t.string,
      ivrJsonStr: t.string,
      publish: t.string,
      version: t.string,
      description: t.string,
      type: t.union([t.literal('0'), t.literal('1'), t.literal('2'), t.literal('3'), t.literal('4')]),
    }),
    Output: t.type({}),
  },
  '/tcccadmin/ivr/uploadIvr': {
    Input: t.type({}),
    Output: t.type({}),
  },
  '/tcccadmin/user/setUserMark': {
    Input: t.type({
      userId: t.string,
      useMark: t.string,
    }),
    Output: t.type({}),
  },
  '/tcccadmin/staff/setConfig': {
    Input: t.partial({
      useMobileCallOut: t.boolean,
      useMobileAcceptType: t.union([t.literal(0), t.literal(1), t.literal(2)]),
    }),
    Output: t.type({}),
  },
  '/tcccadmin/staff/getConfig': {
    Input: t.type({}),
    Output: t.type({
      canUseMobile: t.boolean,
      useMobileCallOut: t.boolean,
      useMobileAcceptType: t.union([t.literal(0), t.literal(1), t.literal(2)]),
    }),
  },
  '/tcccadmin/feedbackqueue/queryQueueCount': {
    Input: t.type({
      userId: t.string,
    }),
    Output: t.type({
      count: numberFromString,
    }),
  },
  '/tcccadmin/staff/setAssignAccept': {
    Input: t.type({
      assignCall: t.boolean,
      assignIM: t.boolean,
    }),
    Output: t.type({}),
  },
  '/tcccadmin/staff/getAssignAccept': {
    Input: t.type({}),
    Output: t.type({
      existCall: t.boolean,
      assignCall: t.boolean,
      existIM: t.boolean,
      assignIM: t.boolean,
    }),
  },
  '/tcccadmin/extension/getList': {
    Input: t.type({
      pageNum: numberFromString,
      pageSize: numberFromString,
    }),
    Output: t.type({
      total: numberFromString,
      extensionList: nullableArray(
        t.type({
          relation: t.string,
        }),
      ),
    }),
  },
  '/tcccadmin/num/getNumberGroupList': {
    Input: t.partial({
      pageNum: t.string,
      pageSize: t.string,
    }),
    Output: t.type({
      numGroupList: nullableArray(
        t.type({
          groupDesc: t.string,
          groupId: t.string,
          groupName: t.string,
          canCalloutCount: t.string,
          numberCount: t.string,
        }),
      ),
      total: numberFromString,
    }),
  },
};
